import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef, EventEmitter,
  forwardRef,
  OnDestroy, Output,
  ViewChild
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import {environment} from '@environments/environment';
import {TranslocoService} from '@jsverse/transloco';
import {Subject, takeUntil} from 'rxjs';

@Component({
  selector: 'app-altcha',
  imports: [
    CommonModule
  ],
  template: `
    <div class="d-flex justify-content-end">
      <altcha-widget
        challengeurl="{{ challengeUrl }}"
        strings="{{ altchaTrans }}"
        #altchaWidget>
      </altcha-widget>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AltchaComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AltchaComponent),
      multi: true
    }
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AltchaComponent implements ControlValueAccessor, Validator, AfterViewInit, OnDestroy {

  @ViewChild('altchaWidget', { static: true }) altchaWidget!: ElementRef;
  @Output() onVerified = new EventEmitter<void>();

  value: string = '';
  onChange: any = () => {};
  onTouched: any = () => {};

  public altchaTrans: string | null;
  public readonly challengeUrl = `${environment.apiUrl}/captcha/challenge`;
  private readonly destroy$: Subject<void> = new Subject();

  public constructor(private readonly transloco: TranslocoService) {
    this.transloco.selectTranslateObject('auth.altcha').pipe(
      takeUntil(this.destroy$)
    ).subscribe(trans => {
      this.altchaTrans = JSON.stringify(trans);
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public ngAfterViewInit(): void {
    const el = this.altchaWidget?.nativeElement as HTMLElement;
    el?.addEventListener('statechange', (ev) => {
      const { detail } = ev as CustomEvent;
      if (detail) {
        const { payload, state } = detail;
        this.onStateChange(state, payload);
      }
    });
  }

  public writeValue(value: any): void {
    this.value = value;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    if (!this.value) {
      return { required: true };
    }
    return null;
  }

  private onStateChange(state: 'unverified' | 'verifying' | 'verified' | 'error', payload: string = '') {
    this.value = state === 'verified' ? payload : '';
    this.onChange(this.value);
    this.onTouched();
    if (state === 'verified') {
      this.onVerified.emit();
    }
  }
}
